'use client';

import type { FragmentType } from '@packages/gql/generated/shopping';
import type { CompactProductFragmentFragmentDoc } from '@packages/gql/generated/shopping/CompactProductFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { getResponsiveAkamaiTemplate } from '@packages/modules/src/helpers/getResponsiveAkamaiTemplate/getResponsiveAkamaiTemplate';
import { PriceDV } from '@packages/modules/src/PriceDV/PriceDV';
import { useDeviceHeader } from '@packages/shared/src/providers/DeviceHeaderProvider/useDeviceHeader';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { TaxHint } from '../TaxHint/TaxHint.client';

/* GraphQL */ `
  fragment CompactProductFragment on Product {
    name
    price {
      ...ProductPriceFragment
    }
    mainMedia: media(limit: 1) {
      url
    }
    delivery {
      ...TaxHintFragment
    }
  }
`;

export type CompactProductProps = {
  /**
   * Price, media and deliveryType **data** from given product.
   */
  data: FragmentType<typeof CompactProductFragmentFragmentDoc>;
  /**
   * Whether to show product name
   * Shows maximal two lines of the name
   * @default false
   */
  withProductName?: boolean;

  /**
   * Whether to show tax hint
   * @default true
   */
  showTaxHint?: boolean;
};

/**
 * CompactProduct component displays the Price and Image of a given product in a condensed layout.
 * */
export const CompactProduct = ({
  data: compactProductData,
  withProductName = false,
  showTaxHint = true,
}: CompactProductProps) => {
  const device = useDeviceHeader();
  const { mainMedia, name, price, delivery } =
    unmask<typeof CompactProductFragmentFragmentDoc>(compactProductData);
  const { templateName, imageDimensions } = getResponsiveAkamaiTemplate('compact', device);

  return (
    <div className="flex flex-row items-start gap-1.5">
      <div className="relative h-[72px] w-[72px] shrink-0 lg:h-[88px] lg:w-[88px]">
        {mainMedia[0]?.url && (
          <Image
            role="presentation"
            src={`${mainMedia[0].url}?${templateName}`}
            unoptimized
            alt=""
            className="h-auto max-h-full w-auto max-w-full object-contain"
            {...imageDimensions}
          />
        )}
      </div>
      <div>
        {withProductName && (
          <p className="text-size-headline-xl text-color-text-dark mb-0.5 line-clamp-2 font-bold">
            {name}
          </p>
        )}
        <PriceDV data={price} />
        {showTaxHint && <TaxHint data={delivery} />}
      </div>
    </div>
  );
};
